/**
 * SMYLE stat counter.
 *
 * Counts a number up to its target value the first time the block scrolls into
 * view. Used by blocks/smyle-stat-counter.liquid.
 *
 * Markup contract:
 *   <smyle-stat-counter data-value="10000000" data-duration="2000" data-separator="."
 *                       data-animate="true" data-decimals="0">
 *     <span data-smyle-stat-value>10.000.000</span>
 *   </smyle-stat-counter>
 *
 * Without JS (or with prefers-reduced-motion) the final value stays in the DOM,
 * so the block never renders empty.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class SmyleStatCounter extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #frame = null;

  #hasRun = false;

  /** @returns {HTMLElement | null} */
  get #output() {
    return this.querySelector('[data-smyle-stat-value]');
  }

  /** @returns {number} */
  get #target() {
    const value = Number.parseFloat(this.dataset.value ?? '');

    return Number.isFinite(value) ? value : 0;
  }

  /** @returns {number} */
  get #duration() {
    const duration = Number.parseInt(this.dataset.duration ?? '', 10);

    return Number.isFinite(duration) && duration > 0 ? duration : 2000;
  }

  /** @returns {number} */
  get #decimals() {
    const decimals = Number.parseInt(this.dataset.decimals ?? '', 10);

    return Number.isFinite(decimals) && decimals > 0 ? Math.min(decimals, 3) : 0;
  }

  /** @returns {string} */
  get #separator() {
    return this.dataset.separator ?? '';
  }

  /** @returns {string} */
  get #decimalSeparator() {
    return this.dataset.decimalSeparator || ',';
  }

  connectedCallback() {
    if (this.dataset.animate !== 'true' || this.#hasRun) return;
    if (window.matchMedia(REDUCED_MOTION_QUERY).matches) return;

    const output = this.#output;

    if (!output || this.#target <= 0) return;

    if (!('IntersectionObserver' in window)) {
      this.#animate();
      return;
    }

    output.textContent = this.#format(0);

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          this.#disconnectObserver();
          this.#animate();
        }
      },
      { threshold: 0.35 },
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#disconnectObserver();

    if (this.#frame !== null) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #animate() {
    const output = this.#output;

    if (!output || this.#hasRun) return;

    this.#hasRun = true;

    const target = this.#target;
    const duration = this.#duration;
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      // easeOutCubic — fast start, soft landing on the final number.
      const eased = 1 - Math.pow(1 - progress, 3);

      output.textContent = this.#format(target * eased);

      if (progress < 1) {
        this.#frame = requestAnimationFrame(step);
      } else {
        this.#frame = null;
        output.textContent = this.#format(target);
      }
    };

    this.#frame = requestAnimationFrame(step);
  }

  /**
   * Groups thousands with the separator configured on the block (e.g. 10.000.000).
   *
   * @param {number} value
   * @returns {string}
   */
  #format(value) {
    const decimals = this.#decimals;
    const fixed = Math.abs(value).toFixed(decimals);
    const [whole, fraction] = fixed.split('.');
    const separator = this.#separator;
    const grouped = separator ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator) : whole;
    const sign = value < 0 ? '-' : '';

    return fraction ? `${sign}${grouped}${this.#decimalSeparator}${fraction}` : `${sign}${grouped}`;
  }
}

if (!customElements.get('smyle-stat-counter')) {
  customElements.define('smyle-stat-counter', SmyleStatCounter);
}
